import { useState, useEffect, useCallback, useRef } from 'react';
import { GoogleMap } from '@react-google-maps/api';
import { useSse } from '../../sse';
import GeoRoundResult from './GeoRoundResult';
import GeoGameOver from './GeoGameOver';
import GeoWaitingResult from './GeoWaitingResult';
import './HostGeoPlay.css';
import { fetchPreviewNextPlayRound } from '../../apiservices/axiosservice/subscriberservice';

import { getSubscriber } from '../../apiservices/LocalStorageService';
import { getCurrentCounter } from '../../utils/timeCounter';
import {
  startGame,
  fetchGameStatusStream,
  fetchQrCode,
} from '../../apiservices/axiosservice/gameservice';
import MusicToggle from './MusicToggle';
import HostGameCode from './HostGameCode';
import { useLeaderboard } from '../../hooks/useLeaderboard';

interface GeoPlayer {
  name: string;
  initials: string;
  color: string;
}

interface GeoRoundData {
  totalRound: number;
  currentRound: number;
  timeAllowed: number;
  targetLocation: {
    cityName: string;
    latitude: number;
    longitude: number;
    locationId: string;
  };
  players: GeoPlayer[];
}

const mockGeoRound: GeoRoundData = {
  // totalRound: 5,
  // currentRound: 1,
  // timeAllowed: 60,
  // targetLocation: {
  //   cityName: 'Paris',
  //   latitude: 40.758,
  //   longitude: -73.9855,
  //   locationId: 'sdeeeeee',
  // }, // Times Square, NYC
  // players: [],
};

interface Props {
  onClose: () => void;
  gameId: string;
}

const HostGeoPlay: React.FC<Props> = ({ onClose, gameId }) => {
  const [roundData, setRoundData] = useState<GeoRoundData>(mockGeoRound);
  const [timeLeft, setTimeLeft] = useState(mockGeoRound.timeAllowed);
  const [roundOver, setRoundOver] = useState(false);
  const [waitingForResult, setWaitingForResult] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const mapRef = useRef<google.maps.Map | null>(null);
  const svRef = useRef<google.maps.StreetViewPanorama | null>(null);
  const user = getSubscriber();
  const [geoResultData, setGeoResultData] = useState<GeoResult>();
  const leaderboardEntry = useLeaderboard(gameId);
  const [currentGameStatus, setCurrentGameStatus] = useState<GameStatusInfo>();
  const [qrCode, setQrCode] = useState<string>('');

  const geoSync = useSse<any>('geo-sync');
  const geoResult = useSse<GeoResult>('geo-result');
  const playerEvent = useSse<any>('player-joined');

  // When map loads, enable Street View panorama on it
  const onMapLoad = useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map;
      const pos = {
        lat: roundData.targetLocation.latitude,
        lng: roundData.targetLocation.longitude,
      };
      const panorama = map.getStreetView();
      panorama.setPosition({ lat: pos.lat, lng: pos.lng });
      panorama.setVisible(true);
      panorama.setOptions({
        addressControl: false,
        showRoadLabels: false,
        fullscreenControl: false,
        zoomControl: true,
      });
      svRef.current = panorama;
    },
    [roundData.targetLocation]
  );

  // Game status
  useEffect(() => {
    fetchGameStatusStream(gameId)
      .then((res) => {
        setCurrentGameStatus(res.data);

        setGameOver(res.data.status === 'FINISHED');
      })
      .catch((error) => {
        console.log('Error fetching game status:', error);
      });
  }, []);

  // Fetch the join QR once per game
  useEffect(() => {
    if (!gameId) return;
    fetchQrCode(gameId)
      .then((res) => setQrCode(res.data.qrCodeBase64Image))
      .catch((error) => console.log('Error fetching QR code:', error));
  }, [gameId]);
  //fetch current round of geogame and set to state
  useEffect(() => {
    const fetchCurrenGeoGametRound = async () => {
      try {
        const response = await fetchPreviewNextPlayRound(gameId);
        setRoundData(response.data);
        setTimeLeft(getCurrentCounter(response.data.currentExpiryTime));
      } catch (error) {
        console.error('Error fetching current GeoGame round:', error);
      }
    };
    fetchCurrenGeoGametRound();
  }, []);

  // trigger start of new round when currentRound state changes (host clicks next round)
  const initiateGame = useCallback(async () => {
    const request: PreviewRemoteRequest = {
      gameId,
      gameOwnerId: user?.userId,
    };

    try {
      const response = await startGame(request);

      if (response.data) {
        setGameOver(response.data.status === 'FINISHED');
      }
    } catch (err) {
      console.log('Error starting game:', err);
    }
  }, []);

  // Update position when roundData changes (SSE new round)
  useEffect(() => {
    if (svRef.current) {
      svRef.current.setPosition({
        lat: roundData.targetLocation.latitude,
        lng: roundData.targetLocation.longitude,
      });
    }
  }, [roundData.targetLocation]);

  useEffect(() => {
    if (geoSync?.targetLocation) {
      setRoundData(geoSync);
      setTimeLeft(getCurrentCounter(geoSync.currentExpiryTime));
      setGeoResultData(undefined);
      setRoundOver(false);
      setWaitingForResult(false);
      setGameOver(false);
    }
  }, [geoSync]);

  //Geo result SSE - if round result received, show round result screen. If game result received, show game result screen
  // const processedResultRef = useRef<number | null>(null);
  useEffect(() => {
    if (geoResult) {
      // processedResultRef.current = geoResult.currentRound;
      setGeoResultData(geoResult);
      setWaitingForResult(false);
      setRoundOver(true);
    }
  }, [geoResult]);

  useEffect(() => {
    if (timeLeft <= 0 && roundData) {
      setWaitingForResult(true);
      return;
    }
    const t = setInterval(() => setTimeLeft((p) => Math.max(0, p - 1)), 1000);
    return () => clearInterval(t);
  }, [timeLeft]);

  if (!roundData?.targetLocation) return null;
  const pos = {
    lat: roundData.targetLocation.latitude,
    lng: roundData.targetLocation.longitude,
  };

  if (gameOver) {
    return (
      <GeoGameOver
        onPlayAgain={onClose}
        leaderBoard={leaderboardEntry}
        gameInfo={currentGameStatus}
      />
    );
  }

  if (roundOver) {
    return (
      <GeoRoundResult
        onNextRound={() => initiateGame()}
        data={geoResultData}
        key={geoResult?.currentRound} // force remount to reset internal state when new result comes in
        leaderBoard={leaderboardEntry}
        gameInfo={currentGameStatus}
      />
    );
  }

  if (waitingForResult) {
    return (
      <GeoWaitingResult
        roundNumber={roundData.currentRound}
        totalRounds={currentGameStatus?.totalRound}
      />
    );
  }

  return (
    <div className="hgeo">
      <MusicToggle />
      <HostGameCode gameId={gameId} qrCode={qrCode} />
      <div className="hgeo__map">
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          center={pos}
          zoom={14}
          onLoad={onMapLoad}
          key={roundData.targetLocation.latitude} // force remount to reset Street View when new location comes in
        />
      </div>

      <div className="hgeo__round-bar">
        <span className="hgeo__round-label">
          Round {roundData.currentRound} / {currentGameStatus?.totalRound}
        </span>
        <span className="hgeo__timer">{timeLeft}</span>
      </div>

      <div className="hgeo__minimap-btn">
        <span className="hgeo__minimap-icon">📍</span>
        <span>Expand 🗺</span>
      </div>
    </div>
  );
};

export default HostGeoPlay;
